import { Injectable } from "@angular/core";
import { environment } from "../../environments/environment";
import { GroupUtils } from "../utils/group-names";

/**
 * Configuration interface for group settings
 */
export interface GroupConfig {
  TurbineRoleGroups: string;
  RamRoleGroups: string;
  LetterUserRoleGroups: string;
  LetterAdminRoleGroups: string;
}

/**
 * Service for managing group configurations from environment settings
 */
@Injectable({
  providedIn: "root",
})
export class GroupConfigService {
  private readonly groupConfig: GroupConfig;

  constructor() {
    this.groupConfig = environment.groups;
  }

  /**
   * Get the group configuration for turbine users
   */
  getTurbineUserGroups(): string {
    return this.groupConfig.TurbineRoleGroups;
  }

  /**
   * Get individual groups for turbine users
   */
  getTurbineUserIndividualGroups(): string[] {
    return GroupUtils.splitGroups(this.getTurbineUserGroups());
  }

  /**
   * Get the group configuration for RAM planners
   */
  getRamPlannerGroups(): string {
    return this.groupConfig.RamRoleGroups;
  }

  /**
   * Get individual groups for RAM planners
   */
  getRamPlannerIndividualGroups(): string[] {
    return GroupUtils.splitGroups(this.getRamPlannerGroups());
  }

  /**
   * Get the group configuration for technical letter viewers
   */
  getTechnicalLetterViewerGroups(): string {
    return this.groupConfig.LetterUserRoleGroups;
  }

  /**
   * Get individual groups for technical letter viewers
   */
  getTechnicalLetterViewerIndividualGroups(): string[] {
    return GroupUtils.splitGroups(this.getTechnicalLetterViewerGroups());
  }

  /**
   * Get the group configuration for technical letter admins
   */
  getTechnicalLetterAdminGroups(): string {
    return this.groupConfig.LetterAdminRoleGroups;
  }

  /**
   * Get individual groups for technical letter admins
   */
  getTechnicalLetterAdminIndividualGroups(): string[] {
    return GroupUtils.splitGroups(this.getTechnicalLetterAdminGroups());
  }

  /**
   * Get all unique individual groups from all role configurations
   */
  getAllIndividualGroups(): string[] {
    const allGroups = [
      ...this.getTurbineUserIndividualGroups(),
      ...this.getRamPlannerIndividualGroups(),
      ...this.getTechnicalLetterViewerIndividualGroups(),
      ...this.getTechnicalLetterAdminIndividualGroups(),
    ];
    return Array.from(new Set(allGroups));
  }

  /**
   * Check if groups configuration is properly loaded
   */
  isConfigurationValid(): boolean {
    return !!(
      this.groupConfig &&
      this.groupConfig.TurbineRoleGroups &&
      this.groupConfig.RamRoleGroups &&
      this.groupConfig.LetterUserRoleGroups &&
      this.groupConfig.LetterAdminRoleGroups
    );
  }

  /**
   * Get the full group configuration object
   */
  getGroupConfig(): GroupConfig {
    return { ...this.groupConfig };
  }
}
